/**
 * 3D COCKPIT's interior treatment and muzzle-flash term, as the handful of TSL
 * nodes `ramp-material.ts`'s walk consumes.
 *
 * `flight/cockpit-interior.ts` owns the arithmetic and imports no `three`; this
 * is the uniforms that carry it, in the same split `cockpit-dash-quad.ts` and
 * `scene-cockpit-3d.ts` use.
 *
 * ## Why it goes into the cosine and not onto the colour
 *
 * The one thing that must not happen is a colour appearing on a 1917 aeroplane
 * that `BARON.PAL` does not contain. Multiplying the walk's *result* by a warm
 * factor — the obvious way to write "a muzzle flash lights the frame" — leaves
 * the palette on the first frame it fires. Adding rungs to the **cosine**, before
 * `shadeLevelFromCosine` quantises it, cannot: whatever comes out is still one
 * of the `2·halfWidth` consecutive entries of that face's own ramp, because
 * that is all the walk can produce. So both halves of this file are expressed
 * in shade rungs and the walk is left alone, which is the same discipline
 * `world/landcover-shading.ts` applies to a field.
 *
 * ## Five uniforms and no lights
 *
 * There are no dynamic lights in this renderer and this does not add one
 * (AD-018 §8): every material stays `MeshBasicNodeMaterial` and nothing is
 * added to the scene graph. What a "flash" costs is one `distance`, one divide
 * and one `add` in the fragment shader **of one material** — the player's own
 * aeroplane, and only while he is sitting in it.
 *
 * ## The gate is a uniform, not a second material
 *
 * 3D COCKPIT can be switched on and off in flight with no reload at all
 * (`scene-boot.ts`'s `applyEffectsSettings` says so in as many words), so the
 * treatment cannot be something a material either has or has not got. `enabled`
 * is a float uniform that is 0 for every frame the player is not in his own 3D
 * cockpit, and at 0 the mix below returns the base cosine **exactly** — the
 * "off is today's pixels" contract the whole toggle row is held to.
 */

import * as THREE from "three/webgpu";
import type { Vec3 } from "@red-opus/engine";
import {
  MUZZLE_FLASH_HALF_DISTANCE_M,
  MUZZLE_FLASH_LEVELS,
  SHADE_LEVELS_PER_COSINE,
  interiorLightFor,
} from "../flight/cockpit-interior.ts";

/** What one frame tells the interior. Every field is read straight off the scene's own state; nothing here is remembered. */
export interface CockpitInteriorFrame {
  /** Whether the player is in his own 3D cockpit this frame — `scene-camera.ts`'s `dashQuadVisible`. */
  readonly enabled: boolean;
  /** The mission's sun (`time-of-day.ts`'s `sunDirectionAt`), or `undefined` at night and for a briefing with no hour. */
  readonly sun: Vec3 | undefined;
  /** The flash's strength, 0..1 (`muzzleFlashStrength`). */
  readonly flashStrength: number;
  /** Where the flash is, in **world** space — the mean of the painted breeches. */
  readonly muzzleWorld: Vec3;
}

/**
 * What the shader is actually bound to this frame — read off the uniform
 * objects the node graph holds, not off the last call that set them.
 *
 * The distinction is phase 1's lesson written into an interface: *"a health
 * check must contain at least one reading that is a property of the system, not
 * of the object"*. "We passed `enabled: false`" is intent; `enabled.value` is
 * the number the GPU will be handed.
 */
export interface CockpitInteriorValues {
  readonly enabled: number;
  readonly bias: number;
  readonly flash: number;
  readonly sun: readonly [number, number, number];
  readonly muzzle: readonly [number, number, number];
}

export interface CockpitInterior {
  /**
   * The cosine the **shipped** walk should use, given the one it would have
   * used: unchanged, plus the muzzle flash.
   */
  cosineNode(base: THREE.Node<"float">): THREE.Node<"float">;
  /**
   * The cosine the **recovered** walk should use for a face the palette marks
   * flat — the mission's own sun, compressed, biased, plus the flash.
   */
  structureCosineNode(): THREE.Node<"float">;
  /** The gate, as a node: `1` while the player is in his own 3D cockpit, `0` otherwise. */
  enabledNode(): THREE.Node<"float">;
  /** One frame's uniforms. Cheap enough to call unconditionally — five scalar writes. */
  update(frame: CockpitInteriorFrame): void;
  /** What the shader is bound to — see `CockpitInteriorValues`. */
  values(): CockpitInteriorValues;
}

export function createCockpitInterior(): CockpitInterior {
  // A float rather than a bool: it multiplies a mix factor, so "off" is the
  // same arithmetic as "on" and neither is a branch.
  const enabled = THREE.TSL.uniform(0);
  const sunScaled = THREE.TSL.uniform(new THREE.Vector3(0, 0, 0));
  const bias = THREE.TSL.uniform(0);
  const flash = THREE.TSL.uniform(0);
  const muzzle = THREE.TSL.uniform(new THREE.Vector3(0, 0, 0));

  /** `flash · falloff(d)`, in cosine — 0 whenever the gate is down, because `flash` is. */
  function flashTerm(): THREE.Node<"float"> {
    const distance = THREE.TSL.positionWorld.distance(muzzle);
    const half = MUZZLE_FLASH_HALF_DISTANCE_M * MUZZLE_FLASH_HALF_DISTANCE_M;
    const falloff = THREE.TSL.float(half).div(distance.mul(distance).add(half));
    return flash.mul(falloff).mul(MUZZLE_FLASH_LEVELS / SHADE_LEVELS_PER_COSINE);
  }

  return {
    cosineNode(base) {
      // The shipped walk's own cosine, plus whatever the guns are doing. The
      // engine's constant sun and its back-face flip are both left alone here:
      // this is the *exterior* of the player's aeroplane — the cowl and the
      // upper wing over the coaming — and it has to shade like every other
      // aeroplane in the sky or his own machine is lit differently from the one
      // he is chasing.
      return base.add(flashTerm());
    },
    structureCosineNode() {
      // And the flat structure's own: the world normal against a sun vector
      // that already carries `INTERIOR_TRANSMITTANCE`, plus the neutral bias,
      // plus the flash. No `faceDirection` — a strut is a closed tube and the
      // pilot sees its outside from every seat, so there is no interior side to
      // flip toward.
      //
      // **`normalWorldGeometry`, not `normalWorld`, and that is a measurement.**
      // `normalWorld` is a module-level singleton three declares with
      // `.toVar('normalWorld')` (three 0.185.1, `nodes/accessors/Normal.js`
      // line 127), and the *second* reference to it in one fragment shader —
      // `lambertCosine` above is the first — came back as a constant far
      // outside the unit sphere: dumped straight to the framebuffer it rendered
      // `(0, 0, 0)` for every fragment of every flat face, i.e. a normal of at
      // most −1 on all three axes. `normalWorldGeometry` is per-fragment and
      // correct in the same shot, and it is the righter node anyway: this is a
      // flat-shaded palette walk over the geometry's own faces, with no normal
      // map anywhere in the app for the interpolated one to differ by.
      return THREE.TSL.normalWorldGeometry.dot(sunScaled).add(bias).add(flashTerm());
    },
    enabledNode() {
      return enabled;
    },
    update(frame) {
      const light = interiorLightFor(frame.enabled ? frame.sun : undefined);
      enabled.value = frame.enabled ? 1 : 0;
      // Held at zero with the feature off so a stale sun cannot survive a
      // toggle: at `enabled = 0` the mix ignores these, and this makes the
      // *uniform* say so too, which is what a probe would read.
      sunScaled.value.set(light.sunScaled.x, light.sunScaled.y, light.sunScaled.z);
      bias.value = frame.enabled ? light.bias : 0;
      flash.value = frame.enabled ? Math.min(1, Math.max(0, frame.flashStrength)) : 0;
      muzzle.value.set(frame.muzzleWorld.x, frame.muzzleWorld.y, frame.muzzleWorld.z);
    },
    values() {
      return {
        enabled: enabled.value,
        bias: bias.value,
        flash: flash.value,
        sun: [sunScaled.value.x, sunScaled.value.y, sunScaled.value.z],
        muzzle: [muzzle.value.x, muzzle.value.y, muzzle.value.z],
      };
    },
  };
}
